<script setup lang="ts">
import { nextTick, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import ArgumentsSearch from '@/components/ArgumentsSearch.vue'
import SearchClaim from '@/components/ClaimSearch.vue'

const argumentsSearchRef = ref<InstanceType<typeof ArgumentsSearch> | null>(null)
// import Vuetest from '@/components/Vuetest.vue'
// import ArgsApp from './components/ArgsApp.vue'

/* ── Claim state — init from route query ── */
const route = useRoute()
const selectedClaimLabel = ref<string | null>((route.query.claim_label as string) ?? null)
const selectedClaimId = ref<string | null>(null)
const hasEverSearched = ref(!!route.query.claim_label)

const stickyClaimBar = ref(false)
const claimBarRef = ref<HTMLElement | null>(null)
const claimBarHeight = ref(0)

/* ── Sync claim label to URL ── */
watch(selectedClaimLabel, (label) => {
  const url = new URL(window.location.href)
  if (label) url.searchParams.set('claim_label', label)
  else url.searchParams.delete('claim_label')
  history.replaceState(null, '', url.toString())
})

watch(stickyClaimBar, (v) => {
  if (v) nextTick(() => { claimBarHeight.value = claimBarRef.value?.offsetHeight ?? 0 })
  else claimBarHeight.value = 0
})

const onSelectClaim = (payload: { id: string; label: string }) => {
  selectedClaimId.value = payload.id
  selectedClaimLabel.value = payload.label
  hasEverSearched.value = true
  nextTick(() => argumentsSearchRef.value?.triggerSearch())
}

const onClaimInputChanged = (value: string) => {
  if (selectedClaimLabel.value && value !== selectedClaimLabel.value) {
    selectedClaimId.value = null
    selectedClaimLabel.value = null
  }
}

/* ── Resolve claim_id from label on mount, then trigger search ── */
async function resolveClaimIdFromLabel(label: string): Promise<string | null> {
  const API_BASE_URL = (import.meta.env.VITE_API_URL || '/api').replace(/\/+$/, '')
  const url = new URL(`${API_BASE_URL}/query-autocomplete`, window.location.origin)
  url.searchParams.set('q', label)
  const res = await fetch(url.toString(), { headers: { Accept: 'application/json' } })
  if (!res.ok) return null
  const data = await res.json() as { hits: { claim_id: string | number; text: string }[] }
  const match = data.hits.find(h => h.text === label) ?? data.hits[0]
  return match ? String(match.claim_id) : null
}

onMounted(async () => {
  if (selectedClaimLabel.value) {
    const id = await resolveClaimIdFromLabel(selectedClaimLabel.value)
    if (id) {
      selectedClaimId.value = id
      nextTick(() => argumentsSearchRef.value?.triggerSearch())
    }
  }
})
</script>

<template>


<!-- <Vuetest/> -->
<!--
<div class="max-w-5xl mx-auto p-4 space-y-4">
  <h1 class="text-4xl font-bold">Args.me - Demo</h1>
</div> -->
  <div ref="claimBarRef" :class="stickyClaimBar ? 'sticky top-0 z-20 bg-white' : ''">
    <SearchClaim
      :initial-label="selectedClaimLabel ?? undefined"
      @select-claim="onSelectClaim"
      @claim-input-changed="onClaimInputChanged"
      @search-requested="nextTick(() => argumentsSearchRef?.triggerSearch())"
    />
  </div>
  <ArgumentsSearch
    v-if="selectedClaimId || hasEverSearched"
    ref="argumentsSearchRef"
    :claim-id="selectedClaimId"
    v-model:sticky-claim-bar="stickyClaimBar"
    :sticky-header-offset="claimBarHeight"
  />
</template>
